import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

const EditJobWorker = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    pin_code: "",
    phoneNo: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (id) {
      fetchWorkerData();
    }
  }, [id]);

  const fetchWorkerData = async () => {
    try {
      setFetching(true);
      setFetchError(null);
      
      const response = await api.post("/job_worker_detail.php", { id });
      
      if (response.data.head.code === 200) {
        const worker = response.data.body;
        setFormData({
          name: worker.name || "",
          address: worker.address || "",
          city: worker.city || "",
          pin_code: worker.pincode || "",
          phoneNo: worker.mobile || "",
        });
      } else {
        setFetchError(response.data.head.msg || "worker not found");
        alert("Failed to fetch worker data: " + response.data.head.msg);
        // navigate("/workers");
      }
    } catch (err) {
      console.error("Error fetching worker data:", err);
      setFetchError("Failed to load worker data. Please try again.");
      alert("Failed to load worker data. Please try again.");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    // if (!formData.address.trim()) newErrors.address = "Address is required";
    // if (!formData.city.trim()) newErrors.city = "City is required";
    // if (!formData.pin_code || !/^\d{6}$/.test(formData.pin_code)) 
    //   newErrors.pin_code = "Valid 6-digit pin code is required";
    if (!formData.phoneNo || !/^\d{10}$/.test(formData.phoneNo)) 
      newErrors.phoneNo = "Valid 10-digit phone number is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setErrors({});
    setSubmitError(null);
    setLoading(true);

    const payload = {
      id: id,
      name: formData.name,
      mobile: formData.phoneNo,
      city: formData.city,
      address: formData.address,
      pincode: formData.pin_code
    };

    try {
      const response = await api.post("/job_worker_edit.php", payload);
      
      if (response.data.head.code === 200) {
        alert("worker updated successfully!");
         navigate("/jobworker");
      } else {
        setSubmitError(response.data.head.msg || "Failed to update worker");
        alert(`Error: ${response.data.head.msg}`);
      }
    } catch (err) {
      console.error("Error updating worker:", err);
      setSubmitError("Failed to update worker. Please try again.");
      alert("Failed to update worker. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? (
      <div className="text-danger small mt-1">{errors[field]}</div>
    ) : null;

  if (fetching) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading job worker data...</p>
        </div>
      </Container>
    );
  }

  if (fetchError) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          {fetchError}
          <Button 
            variant="outline-danger" 
            className="ms-3"
            onClick={() => navigate("/jobworkers")}
          >
            Back to Job Worker List
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Edit Job Worker</h4>
        <Button className="btn-top" onClick={() => window.history.back()}>
          Back
        </Button>
      </div>

      {submitError && (
        <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Worker Name *</Form.Label>
              <Form.Control
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter worker name"
                isInvalid={!!errors.name}
                disabled={loading}
              />
              {renderError("name")}
            </Form.Group>
          </Col>

          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Phone Number *</Form.Label>
              <Form.Control
                name="phoneNo"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                value={formData.phoneNo}
                onChange={handleChange}
                placeholder="10-digit phone number"
                isInvalid={!!errors.phoneNo}
                disabled={loading}
              />
              {renderError("phoneNo")}
            </Form.Group>
          </Col>

          <Col lg={12}>
            <Form.Group>
              <Form.Label>Address *</Form.Label>
              <Form.Control
                name="address"
                as="textarea"
                rows={3}
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter complete address"
                isInvalid={!!errors.address}
                disabled={loading}
              />
              {renderError("address")}
            </Form.Group>
          </Col>

          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>City *</Form.Label>
              <Form.Control
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
                isInvalid={!!errors.city}
                disabled={loading}
              />
              {renderError("city")}
            </Form.Group>
          </Col>

          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Pin Code *</Form.Label>
              <Form.Control
                name="pin_code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={formData.pin_code}
                onChange={handleChange}
                placeholder="6-digit pin code"
                isInvalid={!!errors.pin_code}
                disabled={loading}
              />
              {renderError("pin_code")}
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button 
            type="submit" 
            className="btn-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Job Worker"
            )}
          </Button>
          <Button
            type="button"
            className="btn-top"
            onClick={() => window.history.back()}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default EditJobWorker;